/**
 * Floor plan A4 가로(landscape) PDF — `FloorPlanA4PreviewHorizontal` 본문(왼쪽 도면 · 오른쪽 캡션)에 맞춤.
 */

import React from 'react';
import { Document, Page, View, StyleSheet, Image } from '@react-pdf/renderer';
import { PdfLayout } from '../../shared/enum/EnumRepository';
import { ProjectElementData } from '../../shared/dto/EntityRepository';
import { CvPdfText } from '../about/cvPdfShared';
import logoFit from '../../asset/gnb/logo_fit.png';
import type { FloorPlanCaptionLayout } from './floorPlanA4PreviewTypes';
import {
  buildCaptionSegments,
  floorPlanImageSource,
  FloorPlanPdfCaptions,
} from './documentFloorPlanPdfShared';

/** 가로 시트 Column 단 너비 */
const FLOOR_PLAN_CAPTION_COLUMN_MAX_WIDTH_PT_LANDSCAPE = 120;

const FLOOR_PLAN_FIXED_HEADER_RESERVE_PT = 72;
const FLOOR_PLAN_FIXED_FOOTER_RESERVE_PT = 30;

export interface DocumentFloorPlanHorizontalProps {
  title: string;
  subtitle: string;
  date: string;
  gallery: string;
  location: string;
  pageNumberLabel: string;
  floorImageSrc: string;
  workElements: ProjectElementData[];
  captionLayout: FloorPlanCaptionLayout;
  includeArtist: boolean;
}

const DocumentFloorPlanHorizontal: React.FC<DocumentFloorPlanHorizontalProps> = ({
  title,
  subtitle,
  date,
  gallery,
  location,
  pageNumberLabel: _pageNumberLabel,
  floorImageSrc,
  workElements,
  captionLayout,
  includeArtist,
}) => {
  const captionSegments = buildCaptionSegments(
    workElements,
    captionLayout,
    PdfLayout.Horizontal,
    includeArtist,
  );

  const captionStyleRefs = {
    captionColumnsWrap: PDFStyles.Section.captionColumnsWrap,
    captionRowsWrap: PDFStyles.Section.captionRowsWrap,
    captionColumn: PDFStyles.Section.captionColumn,
    captionRowSingleLine: PDFStyles.Section.captionRowSingleLine,
    captionRowTextWrap: PDFStyles.Section.captionRowTextWrap,
    captionBlock: PDFStyles.Section.captionBlock,
    captionRight: PDFStyles.Section.captionRight,
    captionIndex: PDFStyles.Text.captionIndex,
    captionTitle: PDFStyles.Text.captionTitle,
    captionLine: PDFStyles.Text.captionLine,
    captionRowInline: PDFStyles.Text.captionRowInline,
  };

  return (
    <Document>
      <Page size="A4" orientation="landscape" style={PDFStyles.Page.root}>
        <View style={PDFStyles.Section.pageBody}>
          <View style={PDFStyles.Section.landscapeMainRow}>
            <View style={PDFStyles.Section.floorImageWrap}>
              <Image source={floorPlanImageSource(floorImageSrc)} style={PDFStyles.Image.floorPlan} />
            </View>
            <View style={PDFStyles.Section.captionPane}>
              <View
                style={
                  captionLayout === 'row'
                    ? PDFStyles.Section.captionPaneInnerRow
                    : PDFStyles.Section.captionPaneInnerColumn
                }
              >
                <FloorPlanPdfCaptions
                  captionSegments={captionSegments}
                  captionLayout={captionLayout}
                  includeArtist={includeArtist}
                  s={captionStyleRefs}
                />
              </View>
            </View>
          </View>
        </View>

        <View style={PDFStyles.Section.fixedHeaderLeft} fixed>
          <CvPdfText style={PDFStyles.Text.titleLine}>{title}</CvPdfText>
          <CvPdfText
            style={PDFStyles.Text.metaLine}
            render={({ pageNumber }: { pageNumber: number }) => (pageNumber === 1 ? subtitle : '')}
          />
          <CvPdfText
            style={PDFStyles.Text.metaLine}
            render={({ pageNumber }: { pageNumber: number }) => (pageNumber === 1 ? date : '')}
          />
        </View>

        <View style={PDFStyles.Section.fixedLogoWrap} fixed>
          <Image source={logoFit} style={PDFStyles.Image.logo} />
        </View>

        <View style={PDFStyles.Section.fixedFooterRow} fixed>
          <View style={PDFStyles.Section.footerCell}>
            <CvPdfText style={PDFStyles.Text.footerLeft}>{gallery}</CvPdfText>
          </View>
          <View style={PDFStyles.Section.footerCellCenter}>
            <CvPdfText style={PDFStyles.Text.footerCenter}>{location}</CvPdfText>
          </View>
          <View style={PDFStyles.Section.footerCellRight}>
            <CvPdfText
              style={PDFStyles.Text.footerRight}
              render={({ pageNumber, totalPages }: { pageNumber: number; totalPages: number }) =>
                `${pageNumber} / ${totalPages}`
              }
            />
          </View>
        </View>
      </Page>
    </Document>
  );
};

const PDFTypography = {
  titleLine: { fontSize: 13, fontFamily: 'NotoKR', fontWeight: 600 },
  metaLine: { fontSize: 9, fontFamily: 'NotoKR', fontWeight: 400 },
  captionIndex: { fontSize: 7, fontFamily: 'NotoKR', fontWeight: 500, minWidth: 14 },
  captionTitle: { fontSize: 7, fontFamily: 'NotoKR', fontWeight: 500 },
  captionLine: { fontSize: 6.5, fontFamily: 'NotoKR', fontWeight: 400 },
  footer: { fontSize: 7, fontFamily: 'NotoKR', fontWeight: 400 },
};

const PDFStyles = {
  Page: StyleSheet.create({
    root: {
      paddingTop: 24,
      paddingBottom: 18,
      paddingLeft: 22,
      paddingRight: 22,
      backgroundColor: '#ffffff',
      fontFamily: 'NotoKR',
      fontWeight: 400,
      flexDirection: 'column',
    },
  }),

  Image: StyleSheet.create({
    logo: {
      width: 16,
      height: 16,
      objectFit: 'contain' as const,
    },
    floorPlan: {
      width: '100%',
      height: '100%',
      maxWidth: '100%',
      maxHeight: '100%',
      objectFit: 'contain' as const,
      objectPosition: 'left top' as const,
    },
  }),

  Section: StyleSheet.create({
    pageBody: {
      flex: 1,
      width: '100%',
      minHeight: 0,
      flexDirection: 'column',
      paddingTop: FLOOR_PLAN_FIXED_HEADER_RESERVE_PT,
      paddingBottom: FLOOR_PLAN_FIXED_FOOTER_RESERVE_PT,
    },
    landscapeMainRow: {
      flex: 1,
      width: '100%',
      minHeight: 0,
      flexDirection: 'row',
      alignItems: 'stretch',
      justifyContent: 'flex-start',
    },
    /** 미리보기 `ImgColumn` ~52% */
    floorImageWrap: {
      width: '52%',
      maxWidth: '52%',
      flexGrow: 0,
      flexShrink: 1,
      minWidth: 0,
      minHeight: 0,
      alignItems: 'flex-start',
      justifyContent: 'flex-start',
      /** 도면 영역과 캡션 열 간격 (~기존 8pt 의 3배) */
      paddingRight: 56,
      flexDirection: 'column',
    },
    captionPane: {
      flex: 1,
      minWidth: 0,
      minHeight: 0,
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
    /** Row — 오른쪽 열(`captionPane`) 가로폭 대비 80% */
    captionPaneInnerRow: {
      width: '80%',
      maxWidth: '80%',
      alignSelf: 'flex-start',
      flex: 1,
      minWidth: 0,
      minHeight: 0,
      flexDirection: 'column',
    },
    /** Column — 오른쪽 열 가로폭 대비 95% */
    captionPaneInnerColumn: {
      width: '95%',
      maxWidth: '95%',
      alignSelf: 'flex-start',
      flex: 1,
      minWidth: 0,
      minHeight: 0,
      flexDirection: 'column',
    },
    fixedHeaderLeft: {
      position: 'absolute',
      top: 24,
      left: 22,
      maxWidth: '55%',
      flexDirection: 'column',
    },
    fixedLogoWrap: {
      position: 'absolute',
      top: 24,
      right: 22,
    },
    fixedFooterRow: {
      position: 'absolute',
      bottom: 18,
      left: 22,
      right: 22,
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'flex-end',
    },
    captionColumnsWrap: {
      flexGrow: 1,
      flexDirection: 'row',
      justifyContent: 'flex-start',
      alignItems: 'flex-start',
      width: '100%',
      marginTop: 2,
      paddingTop: 4,
      paddingBottom: 4,
      minHeight: 0,
    },
    captionRowsWrap: {
      flexGrow: 1,
      flexDirection: 'column',
      justifyContent: 'flex-end',
      alignItems: 'flex-start',
      width: '100%',
      marginTop: 2,
      paddingTop: 4,
      paddingBottom: 4,
      minHeight: 0,
    },
    captionColumn: {
      width: FLOOR_PLAN_CAPTION_COLUMN_MAX_WIDTH_PT_LANDSCAPE,
      maxWidth: FLOOR_PLAN_CAPTION_COLUMN_MAX_WIDTH_PT_LANDSCAPE,
      flexGrow: 0,
      flexShrink: 0,
      flexDirection: 'column',
      alignItems: 'flex-start',
      /** 열 사이 간격 = paddingRight + 다음 열 paddingLeft (기존 10+4 → 24+4 ≈ 2배) */
      paddingLeft: 4,
      paddingRight: 18,
    },
    captionRowSingleLine: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      justifyContent: 'flex-start',
      width: '100%',
      minWidth: 0,
      marginBottom: 4,
    },
    captionRowTextWrap: {
      flex: 1,
      minWidth: 0,
      flexDirection: 'column',
    },
    captionBlock: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      width: '100%',
      marginBottom: 8,
    },
    captionRight: {
      flex: 1,
      minWidth: 0,
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
    footerCell: {
      flex: 1,
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
    footerCellCenter: {
      flex: 1,
      flexDirection: 'column',
      alignItems: 'center',
      paddingLeft: 4,
      paddingRight: 4,
    },
    footerCellRight: {
      flex: 1,
      flexDirection: 'column',
      alignItems: 'flex-end',
    },
  }),

  Text: StyleSheet.create({
    titleLine: { ...PDFTypography.titleLine, marginBottom: 0 },
    metaLine: { ...PDFTypography.metaLine, marginBottom: 4 },
    captionIndex: { ...PDFTypography.captionIndex, marginRight: 6 },
    captionTitle: { ...PDFTypography.captionTitle, textAlign: 'left' as const, marginBottom: 1 },
    captionLine: { ...PDFTypography.captionLine, textAlign: 'left' as const },
    captionRowInline: {
      ...PDFTypography.captionLine,
      textAlign: 'left' as const,
      lineHeight: 1.35,
    },
    footerLeft: { ...PDFTypography.footer, textAlign: 'left' as const },
    footerCenter: { ...PDFTypography.footer, textAlign: 'center' as const },
    footerRight: { ...PDFTypography.footer, textAlign: 'right' as const },
  }),
};

export default DocumentFloorPlanHorizontal;
